/**
 * Innovadis custom: URL validation utility
 * Added to comply with AIKIDO security requirements for safe URL handling
 * Validates if a URL is safe to use in href attribute to prevent XSS attacks
 * Only allows: http, https, mailto, tel, and relative paths (/)
 */

interface ISafeUrlOptions {
  allowedProtocols?: string[];
  allowedHosts?: string[];
  allowRelative?: boolean;
}

/**
 * Validates if a URL is safe to use in href attribute
 * Prevents javascript:, data:, vbscript:, and other malicious protocol injections
 * @param url - The URL to validate
 * @param options - Optional allowlist policy for protocols and hosts
 * @returns Safe URL or empty string if invalid
 */
export function getSafeUrl(url: string | undefined, options: ISafeUrlOptions = {}): string {
  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmedUrl = url.trim();
  const allowRelative = options.allowRelative ?? true;

  // Innovadis custom: Allow relative URLs while blocking protocol-relative URLs like //example.com.
  if (allowRelative && trimmedUrl.startsWith("/") && !trimmedUrl.startsWith("//")) {
    return trimmedUrl;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);

    // Innovadis custom: Allow safe protocols only (default) with optional per-use override.
    const safeProtocols = (options.allowedProtocols ?? ["http", "https", "mailto", "tel"]).map((protocol) =>
      protocol.endsWith(":") ? protocol.toLowerCase() : `${protocol.toLowerCase()}:`,
    );

    if (!safeProtocols.includes(parsedUrl.protocol.toLowerCase())) {
      return "";
    }

    // Innovadis custom: Optional host allowlist for stricter sinks (for example WhatsApp links).
    if (options.allowedHosts?.length) {
      const allowedHosts = new Set(options.allowedHosts.map((host) => host.toLowerCase()));
      if (!allowedHosts.has(parsedUrl.hostname.toLowerCase())) {
        return "";
      }
    }

    return parsedUrl.toString();
  } catch {
    return "";
  }
}
